





































































































































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  Ref,
  watch,
} from '@vue/composition-api';
import { useRouter, useFormat } from '@ideavivat/vivat-2';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import relativeTime from 'dayjs/plugin/relativeTime';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonIconTop from '@/components/CommonIconTop.vue';
import CommonDotLevels from '@/components/CommonDotLevels.vue';
import CommonChip from '@/components/CommonChip.vue';
import CompanyCalendar from '@/components/Setting/CompanyCalendar.vue';
import CommonPanelWithDriverAbsenceLog from '@/components/Setting/CommonPanelWithDriverAbsenceLog.vue';

import getDriver from '@/gql/driver/getDriver.gql';
import getDeliveryNotesAsDriverReport from '@/gql/driver/getDeliveryNotesAsDriverReport.gql';
import getDeliveriesAsDriverReport from '@/gql/driver/getDeliveriesAsDriverReport.gql';
import getDataForDriverCalendar from '@/gql/driver/getDataForDriverCalendar.gql';
import getDriverStats from '@/gql/driver/getDriverStats.gql';
import getDriverCashFlowStats from '@/gql/driver/getDriverCashFlowStats.gql';
import getFuelConsumptions from '@/gql/driver/getFuelConsumptions.gql';
import getDriverCashFlows from '@/gql/driver/getDriverCashFlows.gql';
import getVehicles from '@/gql/vehicle/getVehiclesForSelector.gql';
import getPaymentAccounts from '@/gql/paymentAccount/getPaymentAccounts.gql';
import deleteAbsenceLog from '@/gql/driver/deleteAbsenceLog.gql';

import vehicleCashflow from '@/table/records/VehicleCashflow';
import driverWorkOption from '@/table/records/DriverWork';
import driverTravelOption from '@/table/records/DriverTravel';
import driverSatisfactionOption from '@/table/records/DriverSatisfaction';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useAuth from '@/useAuth';
import fuelConsumptionTableOption from '@/table/driver/fuelConsumption';
import usePaymentAccount from '@/usePaymentAccount';
import useCalendar from '@/useCalendar';
import useDriverProfile from '@/useDriverProfile';

dayjs.locale('th');
dayjs.extend(relativeTime);

interface CashFlow {
  id: number;
  name: string;
  type: string;
  account: { id: string; name: string; code: string };
  amount: number;
  date: string;
}

const DriverInformation = defineComponent({
  components: {
    CommonModalWithPreset,
    CommonIconTop,
    CommonDotLevels,
    CommonChip,
    CompanyCalendar,
    CommonPanelWithDriverAbsenceLog,
  },
  setup() {
    const { me } = useAuth();
    const { route, router } = useRouter();
    const {
      DRIVER_FEE_PAYMENT_ACCOUNT,
      DRIVER_ALLOWANCE_PAYMENT_ACCOUNT,
      DRIVER_FINE_PAYMENT_ACCOUNT,
    } = usePaymentAccount();
    const NOW = new Date();
    const {
      farthestDeliveryDistanceIndex, distanceTypeToColor, distanceTypeToChipClass, translateDistanceType,
    } = useDeliveryAreaCode();
    const { mapCashflowStatsIntoMonth } = useDriverProfile();
    const {
      selectedDate,
      allowedToBooking,
      clickDateHandler,
    } = useCalendar();
    const toCurrency = (str: string | number) => useFormat.number(str).currency();
    // const currentMonth = (str: string | number) => useFormat.date().currency();
    const toDecimalFormat = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Calendar Range
    const calendarStart = ref('');
    const calendarEnd = ref('');
    const calendarCurrentMonth = ref('');
    const calendarCurrentYear = ref('');
    function handleDatesRender(event: any) {
      calendarStart.value = useFormat.date(event.view.activeStart).computerShortDate();
      calendarEnd.value = useFormat.date(event.view.activeEnd).computerShortDate();
      calendarCurrentMonth.value = (event.view.currentStart.getMonth() + 1);
      calendarCurrentYear.value = (event.view.currentStart.getFullYear());
    }

    const search = reactive({
      filterDN: '',
    });

    const filterDate = computed(() => ({
      month: calendarCurrentMonth.value,
      year: calendarCurrentYear.value,
    }));

    const defaultStats = {
      deliveryNotesCount: 0, distanceTravelled: 0, deliveriesCount: 0, failedDeliveriesCount: 0, averageDeliveryManCount: 0, leaveCount: 0, nearTrips: 0, mediumTrips: 0, farTrips: 0, extraTrips: 0,
    };
    const { result: getDriverStatsResult } = useQuery(getDriverStats, computed(() => ({
      id: route.value.params.id,
      year: filterDate.value.year || undefined,
      month: filterDate.value.month || undefined,
    })), () => ({ fetchPolicy: 'network-only' }));
    const averageOverviewStats = useResult(getDriverStatsResult, defaultStats, (data) => data.averageOverviewStats);
    const averageScheduleStats = useResult(getDriverStatsResult, defaultStats, (data) => data.averageScheduleStats);
    const overviewStats = useResult(getDriverStatsResult, defaultStats, (data) => data.driver.overviewStats);
    const scheduleStats = useResult(getDriverStatsResult, defaultStats, (data) => data.driver.scheduleStats);

    const defaultDriver = {
      name: '',
      cashFlows: [],
    };
    const driver: Ref<any> = ref(defaultDriver);
    const { result: getDriverResult, loading } = useQuery(getDriver, computed(() => ({
      id: route.value.params.id,
    })), () => ({ fetchPolicy: 'network-only' }));
    const driverResult = useResult(getDriverResult, defaultDriver, (data) => data.driver);
    watch(() => driverResult.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal) {
        driver.value = driverResult.value;
      }
    });

    const computedAddress = computed(() => `${driver.value.address?.address} ${driver.value.address?.province?.nameInThai} ${driver.value.address?.district?.nameInThai ? driver.value.address?.district?.nameInThai : ''} ${driver.value.address?.subdistrict?.nameInThai ? driver.value.address?.subdistrict?.nameInThai : ''}`);

    function getAge(dateOfBirth: string) {
      return dayjs(new Date()).diff(dateOfBirth, 'year');
    }
    function getFullThaiDate(dateOfBirth: string) {
      return useFormat.date(dateOfBirth).displayFullThaiDate();
    }

    // For Chart
    const selectedChartYear = ref(new Date().getFullYear() + 543);
    const defaultCashFlowStats = {
      total: { total: 0, months: [] },
      fee: { total: 0, months: [] },
      fine: { total: 0, months: [] },
      driverAllowance: { total: 0, months: [] },
    };
    const driverCashFlowStats: Ref<any> = ref(defaultCashFlowStats);
    const { result: getDriverCashFlowStatsResult, loading: getDriverCashFlowStatsLoading } = useQuery(getDriverCashFlowStats, computed(() => ({
      id: route.value.params.id,
      year: selectedChartYear.value - 543,
      feeAccountId: DRIVER_FEE_PAYMENT_ACCOUNT,
      driverAllowanceAccountId: DRIVER_ALLOWANCE_PAYMENT_ACCOUNT,
      fineAccountId: DRIVER_FINE_PAYMENT_ACCOUNT,
    })), () => ({ fetchPolicy: 'network-only' }));
    const driverCashFlowStatsResult = useResult(getDriverCashFlowStatsResult, defaultCashFlowStats, (data) => data.driver);
    watch(() => driverCashFlowStatsResult.value, (newVal, oldVal) => {
      if (!getDriverCashFlowStatsLoading.value && newVal !== oldVal) {
        driverCashFlowStats.value = driverCashFlowStatsResult.value;
      }
    });
    const totalExpense = computed(() => useFormat.number(driverCashFlowStats.value.total.total).currency());
    const backwardTenYear = Array.from({ length: 10 }, (x, i) => new Date().getFullYear() + 543 - i);
    const computedTotalCashFlowData = computed(() => {
      if (driverCashFlowStats.value.total.months.length === 0) return [];
      return driverCashFlowStats.value.total.months.map(mapCashflowStatsIntoMonth);
    });
    const computedFeeCashFlowData = computed(() => {
      if (driverCashFlowStats.value.fee.months.length === 0) return [];
      return driverCashFlowStats.value.fee.months.map(mapCashflowStatsIntoMonth);
    });
    const computedFineCashFlowData = computed(() => {
      if (driverCashFlowStats.value.fine.months.length === 0) return [];
      return driverCashFlowStats.value.fine.months.map(mapCashflowStatsIntoMonth);
    });
    const computedDriverAllowanceCashFlowData = computed(() => {
      if (driverCashFlowStats.value.driverAllowance.months.length === 0) return [];
      return driverCashFlowStats.value.driverAllowance.months.map(mapCashflowStatsIntoMonth);
    });

    const areaCharts = computed(() => ({
      options: {
        dataLabels: { enabled: false },
        yaxis: { opposite: false },
      },
      series: [{
        name: 'รายได้ประจำปี',
        data: computedTotalCashFlowData.value,
      }, {
        name: 'เบี้ยเลี้ยง',
        data: computedDriverAllowanceCashFlowData.value,
      }, {
        name: 'ค่าปรับ',
        data: computedFineCashFlowData.value,
      }, {
        name: 'ค่าเที่ยว',
        data: computedFeeCashFlowData.value,
      }],
    }));

    const filterWorkTable = reactive({
      status: '',
      createdAt: { from: undefined, to: undefined },
      deliveryNoteId: '',
    });
    const getDeliveryNotesAsWorkVariables = computed(() => {
      const vars: {[key: string]: any} = {
        id: route.value.params.id,
        filterByStatus: undefined,
        // filterCreatedAtBetween: undefined,
        searchDeliveryNoteId: undefined,
      };
      if (filterWorkTable.status) {
        vars.filterByStatus = `%${filterWorkTable.status}%`;
      }
      // if (filterWorkTable.createdAt.from) {
      //   vars.filterCreatedAtBetween = {
      //     from: `${filterWorkTable.createdAt.from} 00:00:00`,
      //     to: `${filterWorkTable.createdAt.to} 23:59:59`,
      //   };
      // }
      if (filterWorkTable.deliveryNoteId) {
        vars.searchDeliveryNoteId = `%${filterWorkTable.deliveryNoteId}%`;
      }
      return vars;
    });

    const filterTravelTable = reactive({
      status: '',
      createdAt: { from: undefined, to: undefined },
      deliveryNoteId: '',
    });
    const getDeliveryNotesAsTravelVariables = computed(() => {
      const vars: {[key: string]: any} = {
        id: route.value.params.id,
        filterByStatus: undefined,
        filterCreatedAtBetween: undefined,
        searchDeliveryNoteId: undefined,
      };
      if (filterTravelTable.status) {
        vars.filterByStatus = `%${filterTravelTable.status}%`;
      }
      if (filterTravelTable.createdAt.from) {
        vars.filterCreatedAtBetween = {
          from: `${filterTravelTable.createdAt.from} 00:00:00`,
          to: `${filterTravelTable.createdAt.to} 23:59:59`,
        };
      }
      if (filterTravelTable.deliveryNoteId) {
        vars.searchDeliveryNoteId = filterTravelTable.deliveryNoteId;
      }
      return vars;
    });

    const filterSatisfactionTable = reactive({
      orderNumber: '',
      status: '',
      deliveryArea: '',
      deliveryNote: '',
    });
    const getDeliveriesAsSatisfactionVariables = computed(() => {
      const vars: {[key: string]: any} = {
        id: route.value.params.id,
        searchOrderNumber: undefined,
        filterByStatus: undefined,
        filterDeliveryArea: undefined,
      };
      if (filterSatisfactionTable.orderNumber) {
        vars.searchOrderNumber = `%${filterSatisfactionTable.orderNumber}%`;
      }
      if (filterSatisfactionTable.status) {
        vars.filterByStatus = `%${filterSatisfactionTable.status}%`;
      }
      if (filterSatisfactionTable.deliveryArea) {
        vars.filterDeliveryArea = filterSatisfactionTable.deliveryArea;
      }
      return vars;
    });

    const filterCashflowTable = reactive({ searchMaintenance: '', searchOther: '' });
    const getMaintenanceCashflowVariables = computed(() => {
      const vars: {[key: string]: any} = { id: route.value.params.id, filterPaymentAccount: undefined };
      if (filterCashflowTable.searchMaintenance) { vars.filterPaymentAccount = parseInt((filterCashflowTable.searchMaintenance as any).id, 10); }
      return vars;
    });

    // For Calendar
    // function navigateToDeliveryNotesDate(date: Date) {
    //   router.push({
    //     name: 'DeliveryNoteList',
    //     params: {
    //       date: useFormat.date(date).computerShortDate(),
    //     },
    //   });
    // }

    const selectedAbsenceLog = ref({ id: -1 });
    function clickEventHandler(event: { item: {[key: string]: any}; index: number }) {
      if (event.item.type === 'deliveryNote') {
        router.push({
          name: 'DeliveryNoteInformation',
          params: {
            id: event.item.id,
          },
        });
      } else if (event.item.type === 'absenceLog') {
        selectedAbsenceLog.value = { id: event.item.id };
      }
    }
    const filterCalendarDueDate = computed(() => ({
      date: {
        from: `${calendarStart.value}`,
        to: `${calendarEnd.value}`,
      },
      time: {
        from: `${calendarStart.value} 00:00:00`,
        to: `${calendarEnd.value} 23:59:59`,
      },
    }));
    const { result: calendarQueryResult } = useQuery(getDataForDriverCalendar, computed(() => ({
      id: route.value.params.id,
      filterCreatedAtBetween: filterCalendarDueDate.value.time.from ? filterCalendarDueDate.value.time : undefined,
      filterDeliveryDateBetween: filterCalendarDueDate.value.date.from ? filterCalendarDueDate.value.date : undefined,
    })), {
      fetchPolicy: 'cache-and-network',
    });
    const driverCalendarInformation = useResult(calendarQueryResult, { absenceLogs: [], deliveryNotes: [], cashFlows: [] }, (data) => data.driver);
    const events = computed(() => {
      const result = driverCalendarInformation.value.deliveryNotes?.map((elm: any) => {
        const color = distanceTypeToColor(elm.distanceType);
        const distanceArr: any[] = elm.deliveries.map((delivery: any) => delivery.area?.distanceType || '');
        const farthestIndex = farthestDeliveryDistanceIndex(distanceArr);
        return {
          id: elm.id,
          accountId: '',
          amount: 0,
          title: `${farthestIndex !== null ? `${elm.deliveries[farthestIndex]?.area?.code ? `[${elm.deliveries[farthestIndex]?.area?.code}] ` : ''}` : ''}${elm.uniqueId}`,
          start: useFormat.date(elm.deliveryDate).computerShortDate(),
          type: 'deliveryNote',
          color,
        };
      });

      const absences = driverCalendarInformation.value.absenceLogs?.map((elm: any) => ({
        id: elm.id,
        allDay: true,
        title: `[ลาหยุด] ${elm.reason}`,
        start: useFormat.date(elm.effectiveDate).computerShortDate(),
        end: useFormat.date(elm.leaveUntil).computerShortDate(),
        type: 'absenceLog',
        color: 'orange',
      }));
      result.push(...absences);

      // this below use to group up CashFlow when it have same PaymentAccountId
      if (me.company) {
        driverCalendarInformation.value.cashFlows.forEach((cashFlow: CashFlow) => {
          const hitIndex = result.findIndex((cashFlowElm: any) => cashFlowElm.accountId === cashFlow.account.id
            && cashFlowElm.start === cashFlow.date);

          let color = '';
          if (parseInt(cashFlow.account.id, 10) === DRIVER_FEE_PAYMENT_ACCOUNT) color = 'orange';
          else if (parseInt(cashFlow.account.id, 10) === DRIVER_FINE_PAYMENT_ACCOUNT) color = 'pink';
          else if (parseInt(cashFlow.account.id, 10) === DRIVER_ALLOWANCE_PAYMENT_ACCOUNT) color = 'yellow';
          if (hitIndex > -1) {
            result[hitIndex].amount += cashFlow.amount;
            result[hitIndex].title = `${toCurrency(result[hitIndex].amount)} - ${cashFlow.name}`;
          } else {
            result.push({
              id: cashFlow.id,
              accountId: cashFlow.account.id,
              amount: cashFlow.amount,
              title: `${toCurrency(cashFlow.amount)} - ${cashFlow.name}`,
              start: cashFlow.date,
              type: 'cashFlow',
              color,
            });
          }
        });
      }

      return result;
    });

    // find Matching Payment Account
    function matchPaymentAccount(cashFlows: any[], type: number) {
      let item;
      // fee
      if (type === 1) {
        item = cashFlows.find((cashFlow: any) => parseInt(cashFlow.account.id, 10) === DRIVER_FEE_PAYMENT_ACCOUNT);
      }
      // fine
      if (type === 2) {
        item = cashFlows.find((cashFlow: any) => parseInt(cashFlow.account.id, 10) === DRIVER_FINE_PAYMENT_ACCOUNT);
      }
      // driverAllowance
      if (type === 3) {
        item = cashFlows.find((cashFlow: any) => parseInt(cashFlow.account.id, 10) === DRIVER_ALLOWANCE_PAYMENT_ACCOUNT);
      }
      if (item) return toCurrency(item?.amount);
      return toCurrency(0);
    }

    const absenceEditModal = ref(null);
    const absenceDeleteModal = ref(null);
    const deleteAbsenceLogMutation = useMutation(deleteAbsenceLog, () => ({
      refetchQueries: ['getDataForDriverCalendar'],
    }));
    deleteAbsenceLogMutation.onDone(() => {
      selectedAbsenceLog.value = { id: -1 };
    });
    function confirmDeleteAbsenceLog(ctx: any) {
      deleteAbsenceLogMutation.mutate({
        id: ctx.id,
      });
    }

    return {
      me,
      toDecimalFormat,
      distanceTypeToChipClass,
      translateDistanceType,
      search,
      absenceEditModal,
      absenceDeleteModal,
      selectedAbsenceLog,
      confirmDeleteAbsenceLog,

      // Use to display Stats value
      overviewStats,
      scheduleStats,
      averageOverviewStats,
      averageScheduleStats,

      // Use with Charts
      driverCashFlowStats,
      totalExpense,
      selectedChartYear,
      backwardTenYear,
      areaCharts,

      getDeliveryNotesAsDriverReport,
      getDeliveryNotesAsWorkReportResult: (data: any) => data.driver.paginatedDeliveryNotes,
      driverWorkOption,
      getDeliveryNotesAsWorkVariables,
      filterWorkTable,
      driverTravelOption,
      getDeliveryNotesAsTravelVariables,

      getDeliveriesAsDriverReport,
      getDeliveriesAsWorkReportResult: (data: any) => data.driver.paginatedDeliveries,
      driverSatisfactionOption,
      filterSatisfactionTable,
      getDeliveriesAsSatisfactionVariables,

      driver,
      computedAddress,

      getAge,
      getFullThaiDate,

      // Use with calendar
      events,
      handleDatesRender,

      clickEventHandler,

      selectedDate,
      allowedToBooking,
      clickDateHandler,

      matchPaymentAccount,

      // For FuelConsumption
      getFuelConsumptions,
      fuelConsumptionTableOption,
      getFuelConsumptionResult: (data: any) => data.deliveryNotes,

      getVehicles,
      getVehiclesResult: (data: any) => data.vehicles.data,

      // For Cashflow table
      getPaymentAccounts,
      getDriverCashFlows,
      vehicleCashflow,
      filterCashflowTable,
      getMaintenanceCashflowVariables,
    };
  },
});

export default DriverInformation;
